import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import styles from './AvailablePaymentMethods.module.scss';
import messages from './messages';
import visaLogo from '../../assets/images/svg/available-payment-methods/visa.svg';
import masterCardLogo from '../../assets/images/svg/available-payment-methods/master-card.svg';
import shopPayLogo from '../../assets/images/svg/available-payment-methods/shop-pay.svg';
import jcbLogo from '../../assets/images/svg/available-payment-methods/jcb.svg';
import discoverLogo from '../../assets/images/svg/available-payment-methods/discover.svg';
import applePayLogo from '../../assets/images/svg/available-payment-methods/apple-pay.svg';
import amexLogo from '../../assets/images/svg/available-payment-methods/amex.svg';
import acimaLogo from '../../assets/images/svg/available-payment-methods/acima.svg';
import affirmLogo from '../../assets/images/svg/available-payment-methods/affirm.svg';
import amazonPayLogo from '../../assets/images/svg/available-payment-methods/amazon-pay.svg';
import payPalLogo from '../../assets/images/svg/available-payment-methods/paypal.svg';
import purpleFinancingLogo from '../../assets/images/svg/available-payment-methods/purple-financing.svg';
import trueMedLogo from '../../assets/images/svg/available-payment-methods/truemed.svg';
import linkLogo from '../../assets/images/svg/available-payment-methods/link.svg';
import { CartInStore } from '../../slices/cart/types';

import {
  CHARGE_AFTER_MINIMUM_CART_VALUE_CENTAMOUNT,
  STRIPE_EXPRESS_CHECKOUT_FEATURE_TOGGLE,
  STRIPE_REGULAR_CHECKOUT_FEATURE_TOGGLE,
} from '../../utils/common/constants';

const AvailablePaymentMethods = ({
  total,
  isCartComponent = false,
}: {
  total: number
  isCartComponent?: boolean
}) => {
  const { formatMessage } = useIntl();

  const { isDraft } = useSelector(
    (state: { cart: CartInStore }) => state.cart,
  );

  const showShopPayExpressCheckout = window?.purTogglesEnabled(
    'shop_pay_express_checkout',
  );
  const showPaypalButton = window.purTogglesEnabled('paypal_buttons') ?? false;
  const showTrueMedPayment = window?.purTogglesEnabled('truemed_payment') ?? false;
  const showAcimaPayment =
    (window?.purTogglesEnabled('acima_payment') && !isDraft) ?? false;

  const enableChargeAfterRegularCheckout = window?.purTogglesEnabled(
    'charge_after_regular_checkout',
  );
  const showPurpleFinancing =
    isDraft ||
    (enableChargeAfterRegularCheckout &&
      total >= CHARGE_AFTER_MINIMUM_CART_VALUE_CENTAMOUNT);

  const isStripeRegularCheckoutEnabled = window?.purTogglesEnabled?.(
      STRIPE_REGULAR_CHECKOUT_FEATURE_TOGGLE,
    );
  const isStripeExpressCheckoutEnabled = window?.purTogglesEnabled?.(
    STRIPE_EXPRESS_CHECKOUT_FEATURE_TOGGLE,
  );
  const isStripeEnabled = isStripeExpressCheckoutEnabled || isStripeRegularCheckoutEnabled;

  const availablePaymentMethodsInfo = [
    {
      id: 'visa',
      altName: formatMessage(messages.altNameVisa),
      image: visaLogo as unknown as string,
    },
    {
      id: 'mastercard',
      altName: formatMessage(messages.altNameMasterCard),
      image: masterCardLogo as unknown as string,
    },
    {
      id: 'amex',
      altName: formatMessage(messages.altNameAmericanExpress),
      image: amexLogo as unknown as string,
    },
    {
      id: 'discover',
      altName: formatMessage(messages.altNameDiscover),
      image: discoverLogo as unknown as string,
    },
    {
      id: 'jcb',
      altName: formatMessage(messages.altNameJCB),
      image: jcbLogo as unknown as string,
    },
    ...isCartComponent ? [
      {
        id: 'apple-pay',
        altName: formatMessage(messages.altNameApplePay),
        image: applePayLogo as unknown as string,
      },
      ...(showShopPayExpressCheckout ? [{
        id: 'shop-pay',
        altName: formatMessage(messages.altNameShopPay),
        image: shopPayLogo as unknown as string,
      }] : []),
      ...(showPaypalButton ? [{
        id: 'paypal',
        altName: formatMessage(messages.altNamePayPal),
        image: payPalLogo as unknown as string,
      }] : []),
      {
        id: 'affirm',
        altName: formatMessage(messages.altNameAffirm),
        image: affirmLogo as unknown as string,
      },
      ...(showAcimaPayment ? [{
        id: 'acima',
        altName: formatMessage(messages.altNameAcima),
        image: acimaLogo as unknown as string,
      }] : []),
      ...(showTrueMedPayment ? [{
        id: 'truemed',
        altName: formatMessage(messages.altNameTrueMed),
        image: trueMedLogo as unknown as string,
      }] : []),
      {
        id: 'amazon-pay',
        altName: formatMessage(messages.altNameAmazonPay),
        image: amazonPayLogo as unknown as string,
      },
      ...(showPurpleFinancing ? [{ // Also known as ChargeAfter
        id: 'purple-financing',
        altName: formatMessage(messages.altPurpleFinancing),
        image: purpleFinancingLogo as unknown as string,
      }] : []),
      ...(isStripeEnabled ? [{
        id: 'stripe',
        altName: formatMessage(messages.altLink),
        image: linkLogo as unknown as string,
      }] : [])
    ] : [],
  ];

  return (
    <div className={classNames(styles.availablePaymentMethods, {
      [styles.checkoutBlock]: !isCartComponent,
    })} data-testid="available-payment-methods">
      {isCartComponent && (
        <div className={styles.availablePaymentMethodsTitle}>
          {formatMessage(messages.title)}
        </div>
      )}
      <div className={styles.availablePaymentMethodsList}>
        {availablePaymentMethodsInfo.map(({ id, altName, image }) => (
          <div key={id} className={classNames(styles.availablePaymentMethodItem, {
            [styles.checkoutBlock]: !isCartComponent,
          })}>
            <img src={image} alt={altName} aria-label={altName} />
          </div>
        ))}
      </div>
    </div>
  );
};

export default AvailablePaymentMethods;
